// "Our journey" — vertical milestone timeline, newest first. Each milestone is
// anchored by a teal icon node on a connecting rail; the icon is chosen to
// reflect the milestone's content. The year is centred to the node and the
// points sit in a card alongside. Shared by /investor-relations-v2 (Company
// Overview tab) and /about.
type Milestone = { year: string; points: string[] };

// Icon library (24×24 stroke paths). Each milestone picks the first theme whose
// keywords match its points, falling back to a generic flag.
const ICONS = {
  // founding / incorporation
  building: <path d="M3 21h18M6 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16M9 7h.01M15 7h.01M9 11h.01M15 11h.01M9 15h.01M15 15h.01" />,
  // funding / capital / revenue
  coins: <><circle cx="12" cy="12" r="9" /><path d="M12 7v10M9.5 9.5h3.75a1.75 1.75 0 0 1 0 3.5H9.5l3.75 3.5" /></>,
  // ratings / certifications / awards
  award: <><circle cx="12" cy="9" r="5" /><path d="M8.5 13L7 22l5-3 5 3-1.5-9" /></>,
  // deals / agreements / signings
  deal: <><path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" /><path d="M9 13l2 2 4-4" /></>,
  // people / employees / clients
  users: <><path d="M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9.5" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></>,
  // launches / new products
  rocket: <><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" /><path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" /><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" /></>,
  // global / expansion across geographies
  globe: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a15 15 0 0 1 0 18 15 15 0 0 1 0-18z" /></>,
  // generic milestone
  flag: <path d="M4 22V4M4 4l1-.4a7 7 0 0 1 6 .8 7 7 0 0 0 6 .8l3-1.2v10l-3 1.2a7 7 0 0 1-6-.8 7 7 0 0 0-6-.8L4 15" />,
};

const THEMES: { icon: keyof typeof ICONS; re: RegExp }[] = [
  { icon: "building", re: /incorporation of (our|the) company/i },
  { icon: "coins", re: /fundrais|series[- ]|\braise|revenue|capital|₹|\bfund\b|invest/i },
  { icon: "award", re: /credit rating|\biso\b|certification|certified|gdpr|compliant|award/i },
  { icon: "deal", re: /\bdeal\b|agreement|sub-?lease|signed|lease/i },
  { icon: "users", re: /employee|\bclient|\bpeople\b|\bteam\b/i },
  { icon: "globe", re: /\bus\b|americas|global|\bgcc\b|international/i },
  { icon: "rocket", re: /launch/i },
  { icon: "building", re: /expand|centre|center|sq\.? ?ft|portfolio|desk|platform/i },
];

function pickIcon(points: string[]): keyof typeof ICONS {
  const text = points.join(" ");
  for (const t of THEMES) if (t.re.test(text)) return t.icon;
  return "flag";
}

// Teal icon node reflecting the milestone's content — shared with the About
// page's horizontal MilestonesCarousel.
export function MilestoneIcon({ points }: { points: string[] }) {
  return (
    <span className="relative z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-accent-soft text-brand-accent ring-1 ring-brand-line">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        {ICONS[pickIcon(points)]}
      </svg>
    </span>
  );
}

// "Leasable area: X msf" points render as the Evolution-slide bubble instead
// of a text bullet — a filled circle carrying the msf figure, on a seafoam →
// teal → deep-green ramp that darkens (and grows) as the years progress.
const MSF_RAMP = ["#cfe3df", "#b3d3cd", "#8db7b0", "#5f948c", "#447973", "#2f635c", "#1d453f", "#12302b", "#0c231f", "#071815"];

export function splitMsf(points: string[]): {
  rest: string[];
  msf: string | null;
  msfNote: string | null;
} {
  const rest: string[] = [];
  let msf: string | null = null;
  let msfNote: string | null = null;
  for (const pt of points) {
    // Anything after "msf" is a qualifier the editor added — most often a
    // snapshot date. It used to break the match (the pattern ended at msf$),
    // so the line silently fell back to a plain bullet and the badge vanished.
    const m = pt.match(/^leasable area:\s*([\d.]+)\s*msf\b[\s,:—–-]*(.*)$/i);
    if (m) {
      msf = m[1];
      msfNote = m[2].trim() || null;
    } else {
      rest.push(pt);
    }
  }
  return { rest, msf, msfNote };
}

/** progress: 0 = oldest year (lightest, smallest) → 1 = latest (darkest, biggest). */
export function MsfBadge({
  value,
  progress,
  note,
}: {
  value: string;
  progress: number;
  /** Qualifier after the figure, e.g. "as of March, 2026". */
  note?: string | null;
}) {
  const t = Math.max(0, Math.min(1, progress));
  const color = MSF_RAMP[Math.min(MSF_RAMP.length - 1, Math.round(t * (MSF_RAMP.length - 1)))];
  const size = 38 + Math.round(t * 16);
  return (
    <span className="flex items-center gap-3">
      <span
        className="flex shrink-0 items-center justify-center rounded-full text-[13px] font-semibold tracking-tight"
        style={{ width: size, height: size, backgroundColor: color, color: t < 0.25 ? "#16302b" : "#fcffff" }}
      >
        {value}
      </span>
      <span className="mono-meta text-brand-muted">
        MSF LEASABLE AREA
        {note && <span className="ml-1.5 normal-case opacity-70">({note})</span>}
      </span>
    </span>
  );
}

export function MilestonesTimeline({ milestones }: { milestones: Milestone[] }) {
  // Stored oldest first. This layout reads top to bottom, so it shows the
  // newest year first — the opposite of the horizontal carousel, which puts
  // the newest on the right. The progress ramp below already assumes index 0
  // is the latest year, so it stays correct after the flip.
  const ordered = [...milestones].reverse();
  return (
    <div>
      {ordered.map((m, idx) => (
        <div key={m.year} className="relative pb-8 last:pb-0">
          {/* connecting rail (skipped on the last item) */}
          {idx < ordered.length - 1 && (
            <span
              aria-hidden
              className="absolute left-[6px] top-6 bottom-0 w-px bg-brand-line"
            />
          )}
          {/* dot sits on the same line as the year — perfectly centred */}
          <div className="relative z-10 flex items-center gap-4">
            <span aria-hidden className="h-3.5 w-3.5 shrink-0 rounded-full bg-brand-accent" />
            <span className="text-xl font-semibold leading-none tracking-tight text-brand-accent">{m.year}</span>
          </div>
          <div className="ml-[30px] min-w-0">
            <div className="mt-3 rounded-2xl border border-brand-line bg-white p-5">
              {(() => {
                const { rest, msf, msfNote } = splitMsf(m.points);
                // idx 0 is the latest year, so it gets the darkest, largest badge
                const progress = ordered.length > 1 ? (ordered.length - 1 - idx) / (ordered.length - 1) : 1;
                return (
                  <>
                    <ul className="space-y-1.5">
                      {rest.map((pt, i) => (
                        <li key={i} className="flex gap-2.5 text-sm font-normal leading-relaxed text-brand-slate">
                          <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-brand-accent" />
                          <span>{pt}</span>
                        </li>
                      ))}
                    </ul>
                    {msf && (
                      <div className="mt-4 border-t border-brand-line pt-4">
                        <MsfBadge value={msf} progress={progress} note={msfNote} />
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
